export type AutoBidCeilingResult =
  | { ok: true; normalized: number; wasAdjusted: boolean }
  | {
      ok: false;
      reason: "below_min" | "over_buy_now" | "rounded_over_buy_now";
      normalized?: number;
    };

/** Current price base used for increment steps (highest bid or starting price). */
export function autoBidBaseAmount(
  currentBid: number,
  startingPrice: number,
): number {
  return currentBid > 0 ? currentBid : startingPrice;
}

/** Minimum allowed auto-bid ceiling = base + one increment (= nextMinBid). */
export function minAutoBidCeiling(
  currentBid: number,
  startingPrice: number,
  increment: number,
): number {
  return autoBidBaseAmount(currentBid, startingPrice) + increment;
}

/** Round up to nearest valid increment step from base. */
export function normalizeAutoBidCeiling(
  raw: number,
  currentBid: number,
  startingPrice: number,
  increment: number,
): number {
  const base = autoBidBaseAmount(currentBid, startingPrice);
  const min = base + increment;
  if (!Number.isFinite(raw) || raw <= 0) return min;
  if (raw <= base) return min;
  const delta = raw - base;
  const steps = Math.ceil(delta / increment);
  return base + steps * increment;
}

/** Max # of increment steps for slider (1 = min ceiling). */
export const DEFAULT_AUTO_BID_SLIDER_STEPS = 15;

export function maxAutoBidCeiling(
  currentBid: number,
  startingPrice: number,
  increment: number,
  buyNowPrice?: number | null,
  canBuyNow?: boolean,
): number {
  const min = minAutoBidCeiling(currentBid, startingPrice, increment);
  const base = autoBidBaseAmount(currentBid, startingPrice);
  const hasBuyNow =
    !!canBuyNow && buyNowPrice != null && Number.isFinite(buyNowPrice);

  if (hasBuyNow) {
    // Largest valid increment step that does not exceed buy-now
    const steps = Math.floor((buyNowPrice! - base) / increment);
    if (steps < 1) return min;
    return base + steps * increment;
  }

  return base + DEFAULT_AUTO_BID_SLIDER_STEPS * increment;
}

export function ceilingToSliderSteps(
  ceiling: number,
  currentBid: number,
  startingPrice: number,
  increment: number,
): number {
  const base = autoBidBaseAmount(currentBid, startingPrice);
  if (ceiling <= base) return 1;
  return Math.max(1, Math.round((ceiling - base) / increment));
}

export function sliderStepsToCeiling(
  steps: number,
  currentBid: number,
  startingPrice: number,
  increment: number,
): number {
  const base = autoBidBaseAmount(currentBid, startingPrice);
  return base + Math.max(1, steps) * increment;
}

export function resolveAutoBidCeiling(
  raw: number,
  opts: {
    currentBid: number;
    startingPrice: number;
    increment: number;
    buyNowPrice?: number | null;
    canBuyNow?: boolean;
  },
): AutoBidCeilingResult {
  const { currentBid, startingPrice, increment, buyNowPrice, canBuyNow } =
    opts;
  const min = minAutoBidCeiling(currentBid, startingPrice, increment);
  const hasBuyNow =
    canBuyNow && buyNowPrice != null && Number.isFinite(buyNowPrice);

  if (!Number.isFinite(raw) || raw < min) {
    return { ok: false, reason: "below_min" };
  }

  if (hasBuyNow && raw > buyNowPrice!) {
    return { ok: false, reason: "over_buy_now" };
  }

  const normalized = normalizeAutoBidCeiling(
    raw,
    currentBid,
    startingPrice,
    increment,
  );

  if (hasBuyNow && normalized > buyNowPrice!) {
    return {
      ok: false,
      reason: "rounded_over_buy_now",
      normalized,
    };
  }

  return {
    ok: true,
    normalized,
    wasAdjusted: normalized !== raw,
  };
}
